import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  Package,
  TrendingUp,
  FileSpreadsheet,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Save,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import api from '../services/api';
import IconButton from '../components/IconButton';

const Lancamentos = () => {
  const { user } = useAuth();
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLancamento, setEditingLancamento] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingLancamento, setDeletingLancamento] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showXlsDropdown, setShowXlsDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    id_prod: '',
    ref_prod: '',
    desc_prod: '',
    qtd_lanc: '',
    id_tp_est: '',
    id_cont: '',
    id_local: 1,
    id_prat: '',
    id_usu: ''
  });
  
  // Estados para o autocomplete de produtos
  const [produtos, setProdutos] = useState([]);
  const [produtoSearch, setProdutoSearch] = useState('');
  const [showProdutoDropdown, setShowProdutoDropdown] = useState(false);
  const [filteredProdutos, setFilteredProdutos] = useState([]);
  
  // Estados para contagens
  const [contagens, setContagens] = useState([]);
  
  // Estados para locais
  const [locais, setLocais] = useState([]);
  
  // Estados para prateleiras
  const [prateleiras, setPrateleiras] = useState([]);
  
  // Estados para tipos de estoque
  const [tiposEstoque, setTiposEstoque] = useState([]);
  
  // Estados para usuários
  const [usuarios, setUsuarios] = useState([]);
  
  // Referências para navegação com ENTER
  const produtoInputRef = useRef(null);
  const referenciaInputRef = useRef(null);
  const quantidadeInputRef = useRef(null);
  const descricaoInputRef = useRef(null);
  const tipoEstoqueSelectRef = useRef(null);
  const localSelectRef = useRef(null);
  const salvarButtonRef = useRef(null);
  
  // Função para navegar com ENTER
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.type !== 'button' && e.target.tagName !== 'BUTTON') {
      // Se for o campo Quantidade, não interceptar - deixar o event listener específico tratar
      if (e.target === quantidadeInputRef.current) {
        return;
      }
      
      e.preventDefault();
      
      // Encontrar todos os elementos focáveis no modal
      const modal = e.target.closest('.modal-content');
      if (!modal) return;
      
      const focusableElements = modal.querySelectorAll(
        'input:not([readonly]):not([type="hidden"]), select:not([disabled]), textarea:not([readonly])'
      );
      
      const currentIndex = Array.from(focusableElements).indexOf(e.target);
      const nextIndex = currentIndex + 1;
      
      if (nextIndex < focusableElements.length) {
        focusableElements[nextIndex].focus();
      }
    }
  };

  useEffect(() => {
    fetchLancamentos();
    fetchProdutos();
    fetchContagens();
    fetchLocais();
    fetchPrateleiras();
    fetchTiposEstoque();
    fetchUsuarios();
  }, []);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showXlsDropdown && !event.target.closest('.relative')) {
        setShowXlsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showXlsDropdown]);
  
  // Filtrar produtos baseado na pesquisa
  useEffect(() => {
    if (produtoSearch.length > 0) {
      const searchLower = produtoSearch.toLowerCase();
      
      // Filtrar produtos que contêm o termo de busca
      const filtered = produtos.filter(produto =>
        produto.desc_prod?.toLowerCase().includes(searchLower) ||
        produto.cod_prod?.toLowerCase().includes(searchLower) ||
        produto.ref_prod?.toLowerCase().includes(searchLower)
      );
      
      // Ordenar com prioridade para códigos que começam com o termo de busca
      const sorted = filtered.sort((a, b) => {
        const aRefLower = a.ref_prod?.toLowerCase() || '';
        const bRefLower = b.ref_prod?.toLowerCase() || '';
        const aCodLower = a.cod_prod?.toLowerCase() || '';
        const bCodLower = b.cod_prod?.toLowerCase() || '';
        const aDescLower = a.desc_prod?.toLowerCase() || '';
        const bDescLower = b.desc_prod?.toLowerCase() || '';
        
        // Prioridade 1: ref_prod que começa com o termo de busca
        const aStartsWithRef = aRefLower.startsWith(searchLower) ? 0 : 1;
        const bStartsWithRef = bRefLower.startsWith(searchLower) ? 0 : 1;
        
        if (aStartsWithRef !== bStartsWithRef) {
          return aStartsWithRef - bStartsWithRef;
        }
        
        // Prioridade 2: cod_prod que começa com o termo de busca
        const aStartsWithCod = aCodLower.startsWith(searchLower) ? 0 : 1;
        const bStartsWithCod = bCodLower.startsWith(searchLower) ? 0 : 1;
        
        if (aStartsWithCod !== bStartsWithCod) {
          return aStartsWithCod - bStartsWithCod;
        }
        
        // Prioridade 3: desc_prod que começa com o termo de busca
        const aStartsWithDesc = aDescLower.startsWith(searchLower) ? 0 : 1;
        const bStartsWithDesc = bDescLower.startsWith(searchLower) ? 0 : 1;
        
        if (aStartsWithDesc !== bStartsWithDesc) {
          return aStartsWithDesc - bStartsWithDesc;
        }
        
        // Se todos têm a mesma prioridade, ordenar alfabeticamente por descrição
        return aDescLower.localeCompare(bDescLower);
      });
      
      setFilteredProdutos(sorted.slice(0, 10)); // Limitar a 10 resultados
      setShowProdutoDropdown(true);
    } else {
      setFilteredProdutos([]);
      setShowProdutoDropdown(false);
    }
  }, [produtoSearch, produtos]);

  const fetchLancamentos = async () => {
    try {
      const response = await api.get('/lancamento-produto');
      setLancamentos(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar lançamentos:', error);
      toast.error('Erro ao carregar lançamentos');
    } finally {
      setLoading(false);
    }
  };
  
  // Carregar produtos para autocomplete
  const fetchProdutos = async () => {
    try {
      const response = await api.get('/produto');
      setProdutos(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };
  
  // Carregar contagens ativas
  const fetchContagens = async () => {
    try {
      const response = await api.get('/contagem');
      // Filtrar apenas contagens ativas
      const contagensAtivas = response.data.data?.filter(contagem => contagem.ativo_cont === 1) || [];
      setContagens(contagensAtivas);
    } catch (error) {
      console.error('Erro ao carregar contagens:', error);
    }
  };
  
  // Carregar todos os locais
  const fetchLocais = async () => {
    try {
      const response = await api.get('/local');
      setLocais(response.data.data || []);
    } catch (error) {
      // Endpoint /api/local não existe no backend atual; usar fallback silencioso
      setLocais(prev => (prev && prev.length > 0) ? prev : [{ id_local: 1, desc_local: 'Local Padrão' }]);
    }
  };
  
  // Carregar prateleiras (especificamente id_prat = 1)
  const fetchPrateleiras = async () => {
    try {
      const response = await api.get('/prateleira');
      // Filtrar apenas a prateleira com id_prat = 1
      const prateleiraEspecifica = response.data.data?.filter(prateleira => prateleira.id_prat === 1) || [];
      setPrateleiras(prateleiraEspecifica);
    } catch (error) {
      console.error('Erro ao carregar prateleiras:', error);
    }
  };
  
  // Carregar tipos de estoque
  const fetchTiposEstoque = async () => {
    try {
      const response = await api.get('/tipo-estoque');
      setTiposEstoque(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de estoque:', error);
    }
  };

  // Carregar usuários
  const fetchUsuarios = async () => {
    try {
      const response = await userService.getUsers();
      setUsuarios(response.users || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const normalizedSearchTerm = (searchTerm ?? '').toString().toLowerCase();
  const filteredLancamentos = lancamentos.filter(lancamento => {
    const desc = (lancamento.desc_prod ?? '').toString().toLowerCase();
    const idProd = (lancamento.id_prod ?? '').toString().toLowerCase();
    const ref = (lancamento.ref_prod ?? '').toString().toLowerCase();
    return (
      desc.includes(normalizedSearchTerm) ||
      idProd.includes(normalizedSearchTerm) ||
      ref.includes(normalizedSearchTerm)
    );
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const sortedLancamentos = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredLancamentos;
    const arr = [...filteredLancamentos];
    const key = sortConfig.key;

    arr.sort((a, b) => {
      const va = a[key];
      const vb = b[key];
      let cmp = 0;

      if (key === 'dt_cad_lanc') {
        const ta = va ? new Date(va).getTime() : 0;
        const tb = vb ? new Date(vb).getTime() : 0;
        cmp = ta - tb;
      } else if (['id_lanc', 'id_prod', 'qtd_lanc'].includes(key)) {
        const na = Number(va) || 0;
        const nb = Number(vb) || 0;
        cmp = na - nb;
      } else {
        const sa = (va ?? '').toString().toLowerCase();
        const sb = (vb ?? '').toString().toLowerCase();
        cmp = sa.localeCompare(sb);
      }

      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    return arr;
  }, [filteredLancamentos, sortConfig]);

  const handleSort = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        setSortConfig({ key, direction: 'desc' });
      } else if (sortConfig.direction === 'desc') {
        setSortConfig({ key: null, direction: null });
      } else {
        setSortConfig({ key, direction: 'asc' });
      }
    } else {
      setSortConfig({ key, direction: 'asc' });
    }
  };

  const renderSortableHeader = (key, label) => {
    const isActive = sortConfig.key === key;
    const dir = sortConfig.direction;
    const Icon = !isActive || !dir ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;

    return (
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
        <button type="button" onClick={() => handleSort(key)} className="flex items-center gap-1 group">
          <span>{label}</span>
          <Icon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:text-gray-300 dark:group-hover:text-gray-100" />
        </button>
      </th>
    );
  };
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPageOptions = [10, 25, 50, 100];
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem('lancamentos_itemsPerPage');
      const num = saved ? Number(saved) : 10;
      return itemsPerPageOptions.includes(num) ? num : 10;
    } catch {
      return 10;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('lancamentos_itemsPerPage', String(itemsPerPage));
    } catch {}
  }, [itemsPerPage]);

  const totalItems = sortedLancamentos.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalItems / itemsPerPage)), [totalItems, itemsPerPage]);

  useEffect(() => {
    // Sempre que a busca mudar, voltar para a primeira página
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    // Garantir que currentPage não fique fora do intervalo quando itens ou itens por página mudarem
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedLancamentos = useMemo(() => {
    return sortedLancamentos.slice(startIndex, endIndex);
  }, [sortedLancamentos, startIndex, endIndex]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const left = Math.max(1, currentPage - 1);
    const right = Math.min(totalPages, currentPage + 1);

    pages.push(1);
    if (left > 2) pages.push('...');

    for (let i = left; i <= right; i++) {
      if (i !== 1 && i !== totalPages) pages.push(i);
    }

    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  const openModal = (lancamento = null) => {
    setEditingLancamento(lancamento);
    setFormData({
      id_prod: lancamento?.id_prod || '',
      ref_prod: lancamento?.ref_prod || '',
      desc_prod: lancamento?.desc_prod || '',
      qtd_lanc: lancamento?.qtd_lanc || '',
      id_tp_est: lancamento?.id_tp_est || '',
      id_cont: lancamento?.id_cont || '',
      id_local: 1,
      id_prat: lancamento?.id_prat || '',
      id_usu: lancamento?.id_usu || user?.id || ''
    });
    
    // Se estiver editando, buscar a descrição do produto
    if (lancamento?.id_prod) {
      const produto = produtos.find(p => p.id_prod === lancamento.id_prod);
      setProdutoSearch(produto ? `${produto.desc_prod} (${produto.cod_prod})` : '');
    } else {
      setProdutoSearch('');
    }
    
    setShowModal(true);
    
    // Focar no campo de produto para novos lançamentos após o modal abrir
    if (!lancamento) {
      setTimeout(() => {
        produtoInputRef.current?.focus();
      }, 100);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLancamento(null);
    setFormData({
      id_prod: '',
      ref_prod: '',
      desc_prod: '',
      qtd_lanc: '',
      id_tp_est: '',
      id_cont: '',
      id_local: 1,
      id_prat: '',
      id_usu: ''
    });
    setProdutoSearch('');
    setShowProdutoDropdown(false);
  };
  


  // Selecionar produto do dropdown
  const selectProduto = (produto) => {
    // Selecionar a primeira contagem ativa disponível
    const primeiraContagem = contagens.length > 0 ? contagens[0].id_cont : '';
    
    // Selecionar o local padrão (id_local = 1)
    const localPadrao = 1;
    
    // Selecionar a prateleira específica (id_prat = 1)
    const prateleiraEspecifica = prateleiras.length > 0 ? prateleiras[0].id_prat : '';
    
    // Selecionar COMERCIAL (id_tp_est = 1) como padrão
    const tipoEstoquePadrao = 1;
    
    setFormData({ 
      ...formData, 
      id_prod: produto.id_prod,
      ref_prod: produto.ref_prod || '',
      desc_prod: produto.desc_prod || '',
      id_cont: primeiraContagem,
      id_local: localPadrao,
      id_prat: prateleiraEspecifica,
      id_tp_est: tipoEstoquePadrao
    });
    setProdutoSearch(`${produto.desc_prod} (${produto.cod_prod})`);
    setShowProdutoDropdown(false);
    
    // Focar no campo Quantidade após selecionar o produto
    setTimeout(() => {
      quantidadeInputRef.current?.focus();
      
      // Após focar na Quantidade, preparar foco para o botão Salvar
      setTimeout(() => {
        // Aguardar um pouco mais para que o usuário possa inserir a quantidade
        // e então focar no botão Salvar quando pressionar ENTER
        const handleQuantidadeEnter = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            salvarButtonRef.current?.focus();
            quantidadeInputRef.current?.removeEventListener('keydown', handleQuantidadeEnter);
          }
        };
        quantidadeInputRef.current?.addEventListener('keydown', handleQuantidadeEnter);
      }, 50);
    }, 100);
  };
  
  // Lidar com mudança no campo de pesquisa de produto
  const handleProdutoSearchChange = (e) => {
    const value = e.target.value;
    setProdutoSearch(value);
    
    // Se o campo estiver vazio, limpar o ID do produto
    if (!value) {
      setFormData({ ...formData, id_prod: '' });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    console.log('handleSave chamado', formData);
    
    if (!formData.desc_prod || !formData.desc_prod.trim()) {
      toast.error('Descrição é obrigatória');
      console.log('Erro: Descrição vazia');
      return;
    }

    if (!formData.id_prod || !formData.id_prod.toString().trim()) {
      toast.error('ID do produto é obrigatório');
      console.log('Erro: ID do produto vazio');
      return;
    }

    if (!formData.qtd_lanc || !formData.qtd_lanc.toString().trim()) {
      toast.error('Quantidade é obrigatória');
      console.log('Erro: Quantidade vazia');
      return;
    }

    console.log('Validações passaram, iniciando salvamento...');
     setIsSaving(true);
    
    try {
      const dataToSend = {
        ...formData,
        id_usu: user?.id || formData.id_usu
      };

      if (editingLancamento) {
        await api.put(`/lancamento-produto/${editingLancamento.id_lanc}`, dataToSend);
        toast.success('Lançamento atualizado com sucesso!');
        closeModal();
      } else {
        await api.post('/lancamento-produto', dataToSend);
        toast.success('Dados inseridos com sucesso!');
        
        // Limpar apenas os campos para nova inserção, mantendo o modal aberto
        setFormData({
          id_prod: '',
          ref_prod: '',
          desc_prod: '',
          qtd_lanc: '',
          id_tp_est: '',
          id_cont: '',
          id_local: 1,
          id_prat: '',
          id_usu: user?.id || ''
        });
        setProdutoSearch('');
        setShowProdutoDropdown(false);
        
        // Focar no campo de produto para nova inserção
        setTimeout(() => {
          produtoInputRef.current?.focus();
        }, 100);
      }
      
      fetchLancamentos();
    } catch (error) {
      console.error('Erro ao salvar lançamento:', error);
      toast.error('Erro ao salvar lançamento');
      // Em caso de erro, os dados não são limpos (campos mantidos)
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingLancamento) return;
    
    setIsDeleting(true);
    
    try {
      await api.delete(`/lancamento-produto/${deletingLancamento.id_lanc}`);
      toast.success('Lançamento deletado com sucesso!');
      setShowDeleteModal(false);
      setDeletingLancamento(null);
      fetchLancamentos();
    } catch (error) {
      console.error('Erro ao deletar lançamento:', error);
      toast.error('Erro ao deletar lançamento');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (lancamento) => {
    setEditingLancamento(lancamento);
    setFormData({
      id_prod: lancamento.id_prod,
      ref_prod: lancamento.ref_prod,
      desc_prod: lancamento.desc_prod,
      qtd_lanc: lancamento.qtd_lanc,
      id_tp_est: lancamento.id_tp_est,
      id_cont: lancamento.id_cont,
      id_local: lancamento.id_local,
      id_prat: lancamento.id_prat,
      id_usu: lancamento.id_usu
    });
      setShowModal(true);
    };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const generateXLS = async (tipoEstoque) => {
    try {
      let lancamentosFiltrados;

      // Para Estoque Administrativo, usar todos os lançamentos
      if (tipoEstoque === 'Estoque Administrativo') {
        lancamentosFiltrados = lancamentos;
      } else {
        // Para outros tipos, filtrar pelo tipo de estoque
        const tiposEstoque = {
          'Estoque Comercial': 1,
          'Estoque Comercial Completo': 1, // Usar os mesmos lançamentos do Estoque Comercial
          'Estoque Imobilizado': 2
        };

        // Filtrar lançamentos pelo tipo de estoque (comparação flexível para string e number)
        lancamentosFiltrados = lancamentos.filter(lancamento => {
          const idTpEst = parseInt(lancamento.id_tp_est);
          const tipoDesejado = tiposEstoque[tipoEstoque];
          return idTpEst === tipoDesejado;
        });
      }

      if (lancamentosFiltrados.length === 0) {
        toast.error(`Nenhum lançamento encontrado para ${tipoEstoque}`);
        return;
      }

      let dadosExcel;

      // Para Estoque Comercial, Estoque Comercial Completo e Estoque Imobilizado, agrupar por produto e somar quantidades
      if (tipoEstoque === 'Estoque Comercial' || tipoEstoque === 'Estoque Comercial Completo' || tipoEstoque === 'Estoque Imobilizado') {
        // Agrupar produtos por ref_prod e somar quantidades
        const produtosAgrupados = {};
        
        lancamentosFiltrados.forEach(lancamento => {
          const chave = lancamento.id_prod;
          
          if (!produtosAgrupados[chave]) {
            produtosAgrupados[chave] = {
              id_prod: lancamento.id_prod,
              ref_prod: lancamento.ref_prod,
              desc_prod: lancamento.desc_prod,
              quantidade_total: 0
            };
          }
          
          // Somar a quantidade (converter para número)
          const quantidade = parseFloat(lancamento.qtd_lanc) || 0;
          produtosAgrupados[chave].quantidade_total += quantidade;
        });
        
        // Para Estoque Comercial Completo, buscar dados do backend
        if (tipoEstoque === 'Estoque Comercial Completo') {
          try {
            const response = await api.get('/lancamento-produto/estoque-comercial-completo');
            const dadosCompletos = response.data.data || [];
            
            // Limpar produtos agrupados e usar dados do backend
            Object.keys(produtosAgrupados).forEach(key => delete produtosAgrupados[key]);
            
            dadosCompletos.forEach(item => {
              produtosAgrupados[item.id_prod] = {
                id_prod: item.id_prod,
                ref_prod: item.ref_prod,
                desc_prod: item.desc_prod,
                quantidade_total: item.quantidade || 0
              };
            });
          } catch (error) {
            console.error('Erro ao buscar estoque comercial completo:', error);
            toast.error('Erro ao buscar dados do estoque comercial completo');
            return;
          }
        }

        // Converter para array e formatar conforme a imagem
        dadosExcel = Object.values(produtosAgrupados).map((produto, index) => {
          // Buscar o cod_prod da tabela produto onde ref_prod do produto = ref_prod do lançamento
          const produtoEncontrado = produtos.find(p => p.ref_prod === produto.ref_prod);
          let codigoProduto = produtoEncontrado ? produtoEncontrado.cod_prod : '';
          
          // Se não encontrou pelo ref_prod, tentar buscar pelo id_prod
          if (!produtoEncontrado && produto.id_prod) {
            const produtoPorId = produtos.find(p => p.id_prod === produto.id_prod);
            codigoProduto = produtoPorId ? produtoPorId.cod_prod : '';
          }
          
          // Formatar código com 5 dígitos se for numérico
          if (codigoProduto && !isNaN(codigoProduto)) {
            codigoProduto = String(codigoProduto).padStart(5, '0');
          }
          
          const baseData = {
            'Cod.': codigoProduto || '', // cod_prod da tabela produto
            'Referência': produto.ref_prod || '',
            'Cod. de Barras': '', // Coluna em branco conforme solicitado
            'Descrição dos Produtos': produto.desc_prod || '',
            'Quant.': Math.round(produto.quantidade_total), // Valor inteiro
            'Estoque': '0,000' // Sempre 0,000
          };
          
          // Para Estoque Comercial Completo, adicionar colunas extras
          if (tipoEstoque === 'Estoque Comercial Completo') {
            return {
              ...baseData,
              'Diferença': '0,000',
              'Pr. Compra': '0',
              'Tot. Compra': '0',
              'Local': 'ESTOQUE',
              'Prateleira': ''
            };
          } else {
            return {
              ...baseData,
              'Local': 'ESTOQUE', // Conteúdo que estava na coluna Estoque
              'Prateleira': '' // Coluna em branco
            };
          }
        });

      } else if (tipoEstoque === 'Estoque Administrativo') {
        // Para Estoque Administrativo, usar a tabela atual com nome do usuário
        dadosExcel = lancamentosFiltrados.map(lancamento => {
          // Buscar o nome do usuário pelo id_usu
          const usuario = usuarios.find(u => u.id === lancamento.id_usu);
          const nomeUsuario = usuario ? usuario.name : '-';
          
          return {
            'ID': lancamento.id_lanc,
            'ID Produto': lancamento.id_prod || '-',
            'Referência': lancamento.ref_prod || '-',
            'Descrição': lancamento.desc_prod,
            'Quantidade': lancamento.qtd_lanc || '-',
            'Data Cadastro': formatDate(lancamento.dt_cad_lanc),
            'Usuário': nomeUsuario
          };
        });
      } else {
        // Para outros tipos de estoque, manter formato original
        dadosExcel = lancamentosFiltrados.map(lancamento => ({
          'ID': lancamento.id_lanc,
          'ID Produto': lancamento.id_prod || '-',
          'Referência': lancamento.ref_prod || '-',
          'Descrição': lancamento.desc_prod,
          'Quantidade': lancamento.qtd_lanc || '-',
          'Tipo de Estoque': tipoEstoque,
          'Data Cadastro': formatDate(lancamento.dt_cad_lanc)
        }));
      }

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dadosExcel);

      // Configurar largura das colunas para melhor visualização
      if (tipoEstoque === 'Estoque Comercial Completo') {
        ws['!cols'] = [
          { wch: 8 },  // Cod.
          { wch: 12 }, // Referência
          { wch: 15 }, // Cod. de Barras
          { wch: 50 }, // Descrição dos Produtos
          { wch: 10 }, // Quant.
          { wch: 12 }, // Estoque
          { wch: 12 }, // Diferença
          { wch: 12 }, // Pr. Compra
          { wch: 12 }, // Tot. Compra
          { wch: 12 }, // Local
          { wch: 12 }  // Prateleira
        ];
      } else if (tipoEstoque === 'Estoque Comercial' || tipoEstoque === 'Estoque Imobilizado') {
        ws['!cols'] = [
          { wch: 8 },  // Cod.
          { wch: 12 }, // Referência
          { wch: 15 }, // Cod. de Barras
          { wch: 50 }, // Descrição dos Produtos
          { wch: 10 }, // Quant.
          { wch: 12 }, // Estoque
          { wch: 12 }, // Local
          { wch: 12 }  // Prateleira
        ];
      } else if (tipoEstoque === 'Estoque Administrativo') {
        ws['!cols'] = [
          { wch: 8 },  // ID
          { wch: 12 }, // ID Produto
          { wch: 15 }, // Referência
          { wch: 50 }, // Descrição
          { wch: 12 }, // Quantidade
          { wch: 15 }, // Data Cadastro
          { wch: 20 }  // Usuário
        ];
      }

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, tipoEstoque);

      // Gerar nome do arquivo com data atual
      const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const nomeArquivo = `${tipoEstoque.replace(/\s+/g, '_')}_${dataAtual}.xlsx`;

      // Fazer download do arquivo
      XLSX.writeFile(wb, nomeArquivo);

      toast.success(`Arquivo ${nomeArquivo} gerado com sucesso!`);
      setShowXlsDropdown(false);
    } catch (error) {
      console.error('Erro ao gerar XLS:', error);
      toast.error('Erro ao gerar arquivo XLS');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lançamentos</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerencie os lançamentos de produtos</p>
        </div>
        <div className="flex space-x-3">
          {/* Botão Gerar XLS com Dropdown */}
          <div className="relative">
            <IconButton
              icon={FileSpreadsheet}
              onClick={() => setShowXlsDropdown(!showXlsDropdown)}
              variant="success"
              title="Gerar XLS"
            />
            
            {showXlsDropdown && (
              <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                <div className="py-1">
                  <button
                    onClick={() => generateXLS('Estoque Comercial')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Estoque Comercial
                  </button>
                  <button
                    onClick={() => generateXLS('Estoque Comercial Completo')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Estoque Comercial Completo
                  </button>
                  <button
                    onClick={() => generateXLS('Estoque Imobilizado')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Estoque Imobilizado
                  </button>
                  <button
                    onClick={() => generateXLS('Estoque Administrativo')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    Estoque Administrativo
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Novo Lançamento" />
        </div>
      </div>

      {/* Search and Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="md:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar lançamentos..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{lancamentos.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Package className="w-8 h-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Filtrados</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{filteredLancamentos.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {renderSortableHeader('id_lanc', 'ID')}
                {renderSortableHeader('id_prod', 'Codigo')}
                {renderSortableHeader('ref_prod', 'Referência')}
                {renderSortableHeader('desc_prod', 'Descrição')}
                {renderSortableHeader('qtd_lanc', 'Quantidade')}
                {renderSortableHeader('dt_cad_lanc', 'Data Cadastro')}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedLancamentos.map((lancamento) => (
                <tr key={lancamento.id_lanc} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {lancamento.id_lanc}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {lancamento.id_prod || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {lancamento.ref_prod || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                    <div className="max-w-xs truncate">
                      {lancamento.desc_prod}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {lancamento.qtd_lanc || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(lancamento.dt_cad_lanc)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <IconButton icon={Edit3} onClick={() => handleEdit(lancamento)} variant="warning" size="sm" title="Editar" />
                      <IconButton icon={Trash2} onClick={() => {
                          setDeletingLancamento(lancamento);
                          setShowDeleteModal(true);
                        }} variant="danger" size="sm" title="Excluir" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredLancamentos.length === 0 && (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Nenhum lançamento encontrado</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm ? 'Tente ajustar sua busca.' : 'Comece criando um novo lançamento.'}
            </p>
          </div>
        )}

        {filteredLancamentos.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Exibindo <span className="font-medium">{totalItems === 0 ? 0 : startIndex + 1}</span>–<span className="font-medium">{Math.min(endIndex, totalItems)}</span> de <span className="font-medium">{totalItems}</span> registros
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700 dark:text-gray-300">Registros por página:</label>
              <select
                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {itemsPerPageOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <nav className="inline-flex -space-x-px rounded-md shadow-sm items-center" aria-label="Pagination">
              <IconButton
                icon={ChevronsLeft}
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                variant="secondary"
                size="sm"
                title="Primeira página"
              />
              <IconButton
                icon={ChevronLeft}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                variant="secondary"
                size="sm"
                title="Página anterior"
              />

              {pageNumbers.map((p, idx) => (
                <button
                  key={`${p}-${idx}`}
                  onClick={() => typeof p === 'number' && setCurrentPage(p)}
                  disabled={p === '...'}
                  className={`px-3 py-1 border-t border-b border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm transition ${
                    p === currentPage
                      ? 'text-white bg-primary-600 dark:bg-primary-600 border-primary-600'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                  } ${p === '...' ? 'cursor-default' : ''}`}
                >
                  {p}
                </button>
              ))}

              <IconButton
                icon={ChevronRight}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                variant="secondary"
                size="sm"
                title="Próxima página"
              />
              <IconButton
                icon={ChevronsRight}
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                variant="secondary"
                size="sm"
                title="Última página"
              />
            </nav>
          </div>
        )}
      </div>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                {editingLancamento ? 'Editar Lançamento' : 'Novo Lançamento'}
              </h3>
              
              <form onSubmit={handleSave} className="space-y-6 modal-content" onKeyDown={handleKeyDown}>
                {/* Primeira linha - Informações do Produto */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="relative md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Produto *
                    </label>
                    <input
                      ref={produtoInputRef}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      value={produtoSearch}
                      onChange={handleProdutoSearchChange}
                      onFocus={() => setShowProdutoDropdown(true)}

                      placeholder="Pesquise pela descrição do produto"
                      required
                    />
                    
                    {/* Dropdown de produtos */}
                    {showProdutoDropdown && filteredProdutos.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredProdutos.map((produto) => (
                          <div
                            key={produto.id_prod}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                            onClick={() => selectProduto(produto)}
                          >
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {produto.id_prod} - {produto.desc_prod}
                            </div>
                            {produto.ref_prod && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Ref: {produto.ref_prod}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Campo oculto para mostrar o ID selecionado */}
                    {formData.id_prod && (
                      <div className="mt-1 text-xs text-green-600 dark:text-green-400">
                        Produto selecionado: {formData.id_prod}
                      </div>
                    )}
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Referência
                    </label>
                    <input
                      ref={referenciaInputRef}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      value={formData.ref_prod}
                      onChange={(e) => setFormData({ ...formData, ref_prod: e.target.value })}

                      placeholder="Digite a referência"
                    />
                  </div>
                  
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Quantidade *
                    </label>
                    <input
                      type="number"
                      ref={quantidadeInputRef}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      value={formData.qtd_lanc}
                      onChange={(e) => setFormData({ ...formData, qtd_lanc: e.target.value })}

                      placeholder="Digite a quantidade"
                      required
                    />
                  </div>
                </div>
                
                {/* Segunda linha - Descrição (campo maior) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Descrição *
                  </label>
                  <input
                    ref={descricaoInputRef}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    value={formData.desc_prod}
                    onChange={(e) => setFormData({ ...formData, desc_prod: e.target.value })}
                    onKeyDown={(e) => handleKeyDown(e, tipoEstoqueSelectRef)}
                    placeholder="Digite a descrição"
                    required
                  />
                </div>
                
                {/* Terceira linha - IDs de Localização */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo Estoque
                    </label>
                    <select
                      ref={tipoEstoqueSelectRef}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      value={formData.id_tp_est}
                      onChange={(e) => setFormData({ ...formData, id_tp_est: e.target.value })}

                      required
                    >
                      <option value="">Selecione.:</option>
                      {tiposEstoque.map((tipo) => (
                        <option key={tipo.id_tp_est} value={tipo.id_tp_est}>
                          {tipo.desc_tp_est}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contagem
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white cursor-not-allowed"
                      value={formData.id_cont ? 
                        contagens.find(c => c.id_cont === formData.id_cont)?.desc_cont || formData.id_cont
                        : ''}
                      readOnly
                      placeholder="Será preenchido automaticamente"
                    />
                    {/* Campo oculto para o ID da contagem */}
                    <input
                      type="hidden"
                      value={formData.id_cont}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Local
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white cursor-not-allowed"
                      value={locais.find(local => local.id_local === 1)?.desc_local || 'Local Padrão'}
                      readOnly
                      placeholder="Local fixo"
                    />
                    {/* Campo oculto para o ID do local */}
                    <input
                      type="hidden"
                      value={formData.id_local}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Prateleira
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white cursor-not-allowed"
                      value={formData.id_prat ? 
                        prateleiras.find(p => p.id_prat === formData.id_prat)?.desc_prat || formData.id_prat
                        : ''}
                      readOnly
                      placeholder="Será preenchido automaticamente"
                    />
                    {/* Campo oculto para o ID da prateleira */}
                    <input
                      type="hidden"
                      value={formData.id_prat}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <IconButton icon={X} onClick={closeModal} variant="secondary" title="Cancelar" />
                  <IconButton
                    icon={Save}
                    ref={salvarButtonRef}
                    type="submit"
                    disabled={isSaving}
                    variant="primary"
                    title={isSaving ? 'Salvando...' : 'Salvar'}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900">
                <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-2">
                Confirmar Exclusão
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tem certeza que deseja excluir este lançamento? Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <IconButton icon={X} onClick={() => setShowDeleteModal(false)} variant="secondary" title="Cancelar" />
                <IconButton icon={Trash2} onClick={handleDelete} disabled={isDeleting} variant="danger" title={isDeleting ? 'Excluindo...' : 'Excluir'} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lancamentos;